import React, { FC, useRef, useState, useLayoutEffect } from 'react';
import { useDispatch, connect, ConnectedProps } from 'react-redux';
import { useDrag, useDrop, DragPreviewImage } from 'react-dnd';
import _some from 'lodash/some';

import { getMousePosition } from '../../common/getMousePosition';
import CanvasBlock from './CanvasBlock/CanvasBlock';
import * as canvasActions from '../../actions/canvasBlocks';
import * as itemTypes from '../../constants/itemTypes/itemTypes';
import { BEFORE, AFTER } from '../../constants/misc';
import imageSources from '../../constants/imageSources/imageSources';
import { IBlock } from '../../types/content';
import { TAppState } from '../../reducers';
import * as Styled from './styles';
import elementTypes from '../../constants/elementTypes';

interface IProps {
    block: IBlock;
    stateIndex: number;
}

const mapStateToProps = (state: TAppState) => ({
    selectedElements: state.selectedElements.local,
});

const connector = connect(mapStateToProps);
type TProps = IProps & ConnectedProps<typeof connector>;
type TPosition = typeof BEFORE | typeof AFTER;

const CanvasBlockContainer: FC<TProps> = ({ block, stateIndex, selectedElements }) => {
    const dispatch = useDispatch();
    const [hoverPosition, setHoverPosition] = useState<TPosition | null>(null);
    const ref = useRef<HTMLDivElement>(null);
    const containsParagraphElement = _some(selectedElements, ({ type }) => type === elementTypes.PARAGRAPH);

    const [{ isOverCurrent }, drop] = useDrop({
        accept: [itemTypes.STATIC_BLOCK, itemTypes.CUSTOMIZED_BLOCK],
        hover: (item: { stateIndex: number }, monitor) => {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.stateIndex;
            const hoverIndex = stateIndex;
            if (dragIndex === hoverIndex) {
                setHoverPosition(null);
                return;
            }
            const { isInTopHalf, isInBottomHalf } = getMousePosition(ref, monitor);
            if ((isInTopHalf && hoverIndex === dragIndex + 1) || (isInBottomHalf && hoverIndex === dragIndex - 1)) {
                // Cursor is hovering either on the top half of the block under it,
                // or the bottom half of the block above it, so don't show the drop zone bar
                setHoverPosition(null);
            } else {
                setHoverPosition(isInTopHalf ? BEFORE : AFTER);
            }
        },
        drop: (item: { stateIndex: number; newBlock?: IBlock }, monitor: any) => {
            if (!ref.current) {
                return;
            }
            const itemType = monitor.getItemType();
            const dragIndex = item.stateIndex;
            const hoverIndex = stateIndex;
            const { isInTopHalf, isInBottomHalf } = getMousePosition(ref, monitor);

            if (itemType === itemTypes.STATIC_BLOCK) {
                dispatch(
                    canvasActions.addBlock({
                        block: item.newBlock,
                        droppedBlockIdx: hoverPosition === BEFORE ? hoverIndex : hoverIndex + 1,
                    })
                );
            }
            if (itemType === itemTypes.CUSTOMIZED_BLOCK) {
                if (
                    dragIndex === hoverIndex ||
                    (isInTopHalf && hoverIndex === dragIndex + 1) ||
                    (isInBottomHalf && hoverIndex === dragIndex - 1)
                ) {
                    // Target would result in the same placement, so don't do anything
                    return;
                }
                dispatch(
                    canvasActions.swapBlocks({
                        draggedBlockIdx: dragIndex,
                        droppedBlockIdx: hoverIndex,
                        position: hoverPosition,
                    })
                );
            }
        },
        collect: (monitor: any) => ({
            isOverCurrent: monitor.isOver({ shallow: false }),
        }),
    });

    const [{ isDragging }, drag, dragPreview] = useDrag({
        type: block.type,
        item: { stateIndex },
        canDrag: () => !containsParagraphElement && !block.content.structureLocked && block.title !== 'Table',
        collect: (monitor: any) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    // this temporarily fixes the firefox text highlight issue
    // https://github.com/react-dnd/react-dnd/issues/2690
    useLayoutEffect(() => {
        if (ref.current) {
            if (containsParagraphElement) {
                ref.current.setAttribute('draggable', 'false');
            } else {
                ref.current.setAttribute('draggable', 'true');
            }
        }
    }, [containsParagraphElement, ref.current]);

    drag(drop(ref));

    return (
        <div ref={ref}>
            <Styled.DropZoneBar isVisible={isOverCurrent && hoverPosition === BEFORE} />
            <DragPreviewImage src={imageSources.BLOCK_HOVER_IMAGE} connect={dragPreview} />
            <CanvasBlock block={block} isDragging={isDragging} />
            <Styled.DropZoneBar isVisible={isOverCurrent && hoverPosition === AFTER} />
        </div>
    );
};

export default connector(CanvasBlockContainer);
